import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Trophy, 
  Loader2, 
  BarChart3,
  Building2,
  RefreshCw,
  Calendar,
  TrendingUp,
  History,
  FileText,
  Download,
  AlertCircle,
  Target,
  Users,
  AlertTriangle,
  ShieldAlert
} from 'lucide-react';
import { 
  dimensions, 
  maturityLevelNames, 
  calculateEnterpriseResult, 
  buildResultFromAIScores,
  detectImbalances,
  getTotalQuestionCount,
  getDimensionQuestionCount,
  EnterpriseDiagnosticAnswer, 
  EnterpriseDiagnosticResult,
  DimensionId,
  ImbalanceAlert
} from '@/data/enterpriseDiagnosticData';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import {
  useEnterpriseDiagnostics,
  useSaveEnterpriseDiagnostic,
  useEvaluateDiagnostic,
  useLatestEnterpriseDraft,
  useDiscardEnterpriseDraft,
  useEnterpriseDraftAutosave,
} from '@/hooks/useEnterpriseDiagnostic';
import { useAuth } from '@/contexts/AuthContext';
import { useDraftAutosave, loadDraft, clearDraft } from '@/hooks/useDraftAutosave';
import { formatDistanceToNow } from 'date-fns';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

interface EnterpriseInfo {
  nombreEmpresa: string;
  sector: string;
  numEmpleados: string;
  responsable: string;
}

interface EnterpriseDiagnosticDraft {
  step: 'info' | 'dimensions';
  enterpriseInfo: EnterpriseInfo;
  answers: Record<string, EnterpriseDiagnosticAnswer>;
  currentDimensionIndex: number;
}

type ViewMode = 'results' | 'diagnostic';

const dimensionColors: Record<string, string> = {
  L: 'text-primary bg-primary/10 border-primary/30',
  I: 'text-accent bg-accent/10 border-accent/30',
  D: 'text-warning bg-warning/10 border-warning/30',
  E: 'text-success bg-success/10 border-success/30',
  R: 'text-destructive bg-destructive/10 border-destructive/30',
};

const dimensionBgColors: Record<string, string> = {
  L: 'bg-primary',
  I: 'bg-accent',
  D: 'bg-warning',
  E: 'bg-success',
  R: 'bg-destructive',
};

export default function EnterpriseDiagnosticPage() {
  const { user } = useAuth();
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const { isSuperAdmin, showAllOrgs, getOrgName, effectiveOrg } = useOrganizationFilter(selectedOrg);

  const [viewMode, setViewMode] = useState<ViewMode>('results');
  const [step, setStep] = useState<'info' | 'dimensions' | 'complete'>('info');
  const [enterpriseInfo, setEnterpriseInfo] = useState<EnterpriseInfo>({
    nombreEmpresa: '',
    sector: '',
    numEmpleados: '',
    responsable: ''
  });
  const [currentDimensionIndex, setCurrentDimensionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, EnterpriseDiagnosticAnswer>>({});
  const [result, setResult] = useState<EnterpriseDiagnosticResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState<{ updatedAt: string; data: EnterpriseDiagnosticDraft } | null>(null);
  const [draftDecided, setDraftDecided] = useState(false);

  const draftKey = user && effectiveOrg && effectiveOrg !== 'all'
    ? `enterprise_diagnostic_draft_${effectiveOrg}_${user.id}`
    : null;

  // Draft server-side (persistencia dual — localStorage + BD)
  const serverDraftOrgId = effectiveOrg && effectiveOrg !== 'all' ? effectiveOrg : null;
  const { data: serverDraft, isLoading: loadingServerDraft } = useLatestEnterpriseDraft(serverDraftOrgId);
  const discardServerDraft = useDiscardEnterpriseDraft();

  // Load draft on mount or when effectiveOrg changes.
  // IMPORTANTE: solo se ejecuta una vez por draftKey. Si re-ejecutáramos cada
  // vez que cambia serverDraft.updated_at (por el propio autosave), el prompt
  // reaparecería y draftEnabled caería a false, apagando el autosave. Bug real
  // observado 2026-07-24. Usamos ref del draftKey ya procesado.
  const restoredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (restoredKeyRef.current === draftKey) return;  // ya restauramos para este key
    setDraftDecided(false);
    setDraftPrompt(null);
    if (!draftKey) return;
    if (loadingServerDraft) return;  // esperar a que la query del server termine
    restoredKeyRef.current = draftKey;

    const localDraft = loadDraft<EnterpriseDiagnosticDraft>(draftKey);

    const countAnswers = (a: Record<string, EnterpriseDiagnosticAnswer> | null | undefined) =>
      a ? Object.values(a).filter(v => v && (v.text?.trim() || v.cannotAnswer)).length : 0;

    const localAnswerCount = countAnswers(localDraft?.data?.answers);
    const serverAnswerCount = countAnswers(serverDraft?.answers);

    const hasLocalContent = !!localDraft?.data && (
      localAnswerCount > 0 ||
      Object.values(localDraft.data.enterpriseInfo || {}).some(v => v && String(v).trim().length > 0)
    );

    const hasServerContent = !!serverDraft && (
      serverAnswerCount > 0 ||
      !!(serverDraft.company_name || serverDraft.sector || serverDraft.employee_count)
    );

    if (!hasLocalContent && !hasServerContent) {
      setDraftDecided(true);
      return;
    }

    // Elegir el draft con MÁS respuestas (evita que un server con solo info-empresa
    // pise el local con respuestas). Empate en answers → el más reciente por timestamp.
    const localTs = hasLocalContent ? new Date(localDraft!.updatedAt).getTime() : 0;
    const serverTs = hasServerContent ? new Date(serverDraft!.updated_at).getTime() : 0;

    let winner: 'local' | 'server' | null = null;
    if (hasLocalContent && !hasServerContent) winner = 'local';
    else if (!hasLocalContent && hasServerContent) winner = 'server';
    else if (localAnswerCount > serverAnswerCount) winner = 'local';
    else if (serverAnswerCount > localAnswerCount) winner = 'server';
    else winner = serverTs >= localTs ? 'server' : 'local';

    if (winner === 'server') {
      // Merge: campos del server con fallback a local para lo que server no tenga.
      const mergedAnswers = { ...(localDraft?.data?.answers ?? {}), ...(serverDraft!.answers ?? {}) };
      const combined: EnterpriseDiagnosticDraft = {
        step: Object.keys(mergedAnswers).length > 0 ? 'dimensions' : 'info',
        enterpriseInfo: {
          nombreEmpresa: serverDraft!.company_name ?? localDraft?.data?.enterpriseInfo?.nombreEmpresa ?? '',
          sector: serverDraft!.sector ?? localDraft?.data?.enterpriseInfo?.sector ?? '',
          numEmpleados: serverDraft!.employee_count ?? localDraft?.data?.enterpriseInfo?.numEmpleados ?? '',
          responsable: localDraft?.data?.enterpriseInfo?.responsable ?? '',
        },
        answers: mergedAnswers,
        currentDimensionIndex: localDraft?.data?.currentDimensionIndex ?? 0,
      };
      setDraftPrompt({ updatedAt: serverDraft!.updated_at, data: combined });
    } else {
      setDraftPrompt(localDraft!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, loadingServerDraft]);

  const handleResumeEnterpriseDraft = () => {
    if (!draftPrompt) return;
    const d = draftPrompt.data;
    setEnterpriseInfo(d.enterpriseInfo);
    setAnswers(d.answers || {});
    setCurrentDimensionIndex(d.currentDimensionIndex || 0);
    setStep(d.step);
    setViewMode('diagnostic');
    setDraftPrompt(null);
    setDraftDecided(true);
    toast.success('Diagnóstico restaurado');
  };

  const handleDiscardEnterpriseDraft = () => {
    if (draftKey) clearDraft(draftKey);
    if (serverDraftOrgId) discardServerDraft.mutate(serverDraftOrgId);
    setDraftPrompt(null);
    setDraftDecided(true);
  };

  const draftEnabled =
    !!draftKey && draftDecided && (step === 'info' || step === 'dimensions') && !draftPrompt && viewMode === 'diagnostic';
  const draftValue: EnterpriseDiagnosticDraft = {
    step: step === 'dimensions' ? 'dimensions' : 'info',
    enterpriseInfo,
    answers,
    currentDimensionIndex,
  };
  // Autosave dual: localStorage (rápido, 500ms) + server (backup, 3s)
  useDraftAutosave(draftKey, draftValue, draftEnabled);
  useEnterpriseDraftAutosave(
    draftEnabled && !!serverDraftOrgId,
    serverDraftOrgId ? {
      organizationId: serverDraftOrgId,
      companyName: enterpriseInfo.nombreEmpresa,
      sector: enterpriseInfo.sector,
      employeeCount: enterpriseInfo.numEmpleados,
      answers,
    } : null,
  );
  // DB hooks
  const orgFilter = selectedOrg !== 'all' ? selectedOrg : (isSuperAdmin ? undefined : effectiveOrg);
  const { data: diagnosticHistory = [], isLoading: isLoadingHistory } = useEnterpriseDiagnostics(orgFilter);
  const saveDiagnostic = useSaveEnterpriseDiagnostic();
  const evaluateDiagnostic = useEvaluateDiagnostic();

  // Pre-load organization details
  const orgIdForDetails = effectiveOrg && effectiveOrg !== 'all' ? effectiveOrg : undefined;
  const { data: orgDetails } = useQuery({
    queryKey: ['org-details-diagnostic', orgIdForDetails],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('name, sector, employee_count, contact_name')
        .eq('id', orgIdForDetails!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!orgIdForDetails,
  });

  // Pre-fill enterprise info when org details load
  useEffect(() => {
    if (orgDetails && step === 'info') {
      const empCount = orgDetails.employee_count;
      let empRange = '';
      if (empCount) {
        if (empCount <= 10) empRange = '1-10';
        else if (empCount <= 50) empRange = '11-50';
        else if (empCount <= 200) empRange = '51-200';
        else if (empCount <= 500) empRange = '201-500';
        else if (empCount <= 1000) empRange = '501-1000';
        else empRange = '1000+';
      }
      setEnterpriseInfo(prev => ({
        nombreEmpresa: orgDetails.name || prev.nombreEmpresa,
        sector: orgDetails.sector || prev.sector,
        numEmpleados: empRange || prev.numEmpleados,
        responsable: orgDetails.contact_name || prev.responsable,
      }));
    }
  }, [orgDetails, step]);

  const latestResult = diagnosticHistory[0] || null;
  const totalQuestions = getTotalQuestionCount();
  const currentDimension = dimensions[currentDimensionIndex];

  // Map DB record to display format
  const getLatestDimensionScores = () => {
    if (!latestResult?.dimension_scores) return [];
    const scores = latestResult.dimension_scores as any[];
    if (Array.isArray(scores)) return scores;
    return [];
  };

  const handleExportPDF = async () => {
    if (!latestResult) {
      toast.error('No hay diagnóstico para exportar');
      return;
    }

    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const dimScores = getLatestDimensionScores() as any[];

    // --- Header ---
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text('Diagnóstico de Madurez: Marco LIDER-IA', 14, 22);

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`${latestResult.company_name || ''} - ${latestResult.sector || ''}`, 14, 30);

    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 14, 36);

    // --- Resumen General ---
    let y = 46;
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Resumen General', 14, y);
    y += 8;

    const summaryItems = [
      { label: 'Nivel Global', value: `Nivel ${latestResult.overall_level}: ${latestResult.overall_level_name || ''}` },
      { label: 'Completado', value: `${latestResult.completion_percentage || 0}%` },
      { label: 'Empresa', value: latestResult.company_name || '' },
      { label: 'Sector', value: latestResult.sector || '' },
      { label: 'Empleados', value: latestResult.employee_count || '' },
      { label: 'Fecha', value: latestResult.completed_at ? format(new Date(latestResult.completed_at), "dd 'de' MMMM, yyyy", { locale: es }) : 'N/A' },
    ];
    summaryItems.forEach(item => {
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`${item.label}:`, 14, y);
      doc.setTextColor(40, 40, 40);
      doc.text(String(item.value), 60, y);
      y += 6;
    });

    // --- Nivel de Madurez Diagnosticado ---
    y += 6;
    const diagLevel = latestResult.overall_level || 1;
    const diagLevelInfo = maturityLevelNames[diagLevel];
    if (diagLevelInfo) {
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Resultado del Diagnóstico IA', 14, y);
      y += 7;
      doc.setFontSize(12);
      doc.setTextColor(79, 70, 229);
      doc.text(`Nivel ${diagLevel}: ${diagLevelInfo.name}`, 14, y);
      y += 6;
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const descLines = doc.splitTextToSize(diagLevelInfo.description, pageWidth - 28);
      doc.text(descLines, 14, y);
      y += descLines.length * 5 + 4;
    }

    // --- Referencia de Niveles de Madurez ---
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text('Escala de Madurez LIDER-IA', 14, y);
    y += 6;
    [1, 2, 3, 4, 5].forEach(level => {
      const info = maturityLevelNames[level];
      const isCurrentLevel = level === diagLevel;
      doc.setFontSize(9);
      if (isCurrentLevel) {
        doc.setTextColor(79, 70, 229);
        doc.text(`► Nivel ${level}: ${info.name}`, 16, y);
      } else {
        doc.setTextColor(60, 60, 60);
        doc.text(`Nivel ${level}: ${info.name}`, 16, y);
      }
      y += 4;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      const lvlLines = doc.splitTextToSize(info.description, pageWidth - 34);
      doc.text(lvlLines, 20, y);
      y += lvlLines.length * 3.5 + 3;
    });

    // --- Tabla de Dimensiones ---
    if (y > 220) { doc.addPage(); y = 20; }
    y += 6;
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Puntuación por Dimensión', 14, y);
    y += 4;

    if (dimScores.length > 0) {
      const dimTableData = dimScores.map((ds: any) => {
        const dim = dimensions.find(d => d.id === ds.dimensionId);
        const levelName = maturityLevelNames[ds.score]?.name || '';
        return [
          ds.dimensionId,
          dim?.fullName || ds.name || '',
          `${ds.score}/5`,
          levelName,
        ];
      });

      autoTable(doc, {
        head: [['Dim.', 'Nombre', 'Score', 'Nivel']],
        body: dimTableData,
        startY: y,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' },
          2: { cellWidth: 20, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // --- Radar Chart (Pentágono LIDER-IA) — always on a fresh page ---
    if (dimScores.length === 5) {
      doc.addPage();
      y = 20;

      const centerX = pageWidth / 2;
      const radarRadius = 45;
      const centerY = y + radarRadius + 18;
      const dimOrder = ['L', 'I', 'D', 'E', 'R'];
      const orderedScores = dimOrder.map(id => {
        const ds = dimScores.find((s: any) => s.dimensionId === id);
        return ds ? ds.score : 0;
      });

      doc.setFontSize(12);
      doc.setTextColor(40, 40, 40);
      doc.text('Perfil de Madurez LIDER-IA', 14, y);
      doc.setFontSize(9);
      doc.setTextColor(130, 130, 130);
      doc.text('Puntuación por dimensión (escala 1-5)', 14, y + 5);

      // Angle offset: start from top (-90°)
      const angleOffset = -Math.PI / 2;
      const getPoint = (index: number, scale: number) => {
        const angle = angleOffset + (2 * Math.PI * index) / 5;
        return {
          x: centerX + radarRadius * scale * Math.cos(angle),
          y: centerY + radarRadius * scale * Math.sin(angle),
        };
      };

      // Draw grid rings (1-5)
      for (let level = 1; level <= 5; level++) {
        const scale = level / 5;
        doc.setDrawColor(210, 210, 215);
        doc.setLineWidth(0.2);
        for (let i = 0; i < 5; i++) {
          const p1 = getPoint(i, scale);
          const p2 = getPoint((i + 1) % 5, scale);
          doc.line(p1.x, p1.y, p2.x, p2.y);
        }
      }

      // Draw axis lines
      doc.setDrawColor(200, 200, 205);
      doc.setLineWidth(0.2);
      for (let i = 0; i < 5; i++) {
        const p = getPoint(i, 1);
        doc.line(centerX, centerY, p.x, p.y);
      }

      // Draw level numbers on top axis
      for (let level = 0; level <= 5; level++) {
        const scale = level / 5;
        const p = getPoint(0, scale);
        doc.setFontSize(6);
        doc.setTextColor(160, 160, 160);
        doc.text(String(level), p.x + 2, p.y + 1);
      }

      // Draw filled polygon for scores
      const scorePoints: { x: number; y: number }[] = [];
      for (let i = 0; i < 5; i++) {
        const scale = orderedScores[i] / 5;
        scorePoints.push(getPoint(i, scale));
      }

      // Fill polygon
      doc.setFillColor(45, 130, 130);
      doc.setGState(new (doc as any).GState({ opacity: 0.2 }));
      const fillLines: number[] = [];
      scorePoints.forEach(p => { fillLines.push(p.x, p.y); });
      // Use triangle fan to fill pentagon
      for (let i = 1; i < 4; i++) {
        doc.triangle(
          scorePoints[0].x, scorePoints[0].y,
          scorePoints[i].x, scorePoints[i].y,
          scorePoints[i + 1].x, scorePoints[i + 1].y,
          'F'
        );
      }
      doc.setGState(new (doc as any).GState({ opacity: 1 }));

      // Draw score polygon outline
      doc.setDrawColor(30, 110, 110);
      doc.setLineWidth(0.8);
      for (let i = 0; i < 5; i++) {
        const p1 = scorePoints[i];
        const p2 = scorePoints[(i + 1) % 5];
        doc.line(p1.x, p1.y, p2.x, p2.y);
      }

      // Draw dimension labels
      const dimLabels = ['L', 'I', 'D', 'E', 'R'];
      const labelOffset = 8;
      doc.setFontSize(12);
      doc.setTextColor(40, 40, 40);
      for (let i = 0; i < 5; i++) {
        const p = getPoint(i, 1);
        const angle = angleOffset + (2 * Math.PI * i) / 5;
        const lx = p.x + labelOffset * Math.cos(angle);
        const ly = p.y + labelOffset * Math.sin(angle);
        doc.text(dimLabels[i], lx - 3, ly + 3);
      }

      y = centerY + radarRadius + 20;
    }

    // --- Barra visual por dimensión ---
    if (dimScores.length > 0) {
      const barColors: Record<string, [number, number, number]> = {
        L: [79, 70, 229],
        I: [236, 72, 153],
        D: [245, 158, 11],
        E: [34, 197, 94],
        R: [239, 68, 68],
      };

      dimScores.forEach((ds: any) => {
        const dim = dimensions.find(d => d.id === ds.dimensionId);
        const barWidth = 120;
        const filledWidth = (ds.score / 5) * barWidth;
        const color = barColors[ds.dimensionId] || [100, 100, 100];

        doc.setFontSize(9);
        doc.setTextColor(40, 40, 40);
        doc.text(`${ds.dimensionId}: ${dim?.name || ''}`, 14, y);
        // Background bar
        doc.setFillColor(230, 230, 235);
        doc.roundedRect(55, y - 3.5, barWidth, 5, 1, 1, 'F');
        // Filled bar
        if (filledWidth > 0) {
          doc.setFillColor(color[0], color[1], color[2]);
          doc.roundedRect(55, y - 3.5, filledWidth, 5, 1, 1, 'F');
        }
        doc.setFontSize(9);
        doc.text(`${ds.score}/5`, 55 + barWidth + 4, y);
        y += 8;
      });
      y += 4;
    }

    // --- Subdimensiones ---
    const hasSubScores = dimScores.some((ds: any) => ds.subDimensionScores?.length > 0);
    if (hasSubScores) {
      // Check if we need a new page
      if (y > 230) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Detalle por Subdimensión', 14, y);
      y += 4;

      const subTableData: string[][] = [];
      dimScores.forEach((ds: any) => {
        const subs = ds.subDimensionScores || [];
        subs.forEach((sub: any) => {
          subTableData.push([
            sub.subDimensionId,
            sub.subDimensionName,
            `${sub.score}/5`,
            (sub.justification || '').substring(0, 100) + ((sub.justification || '').length > 100 ? '...' : ''),
          ]);
        });
      });

      if (subTableData.length > 0) {
        autoTable(doc, {
          head: [['ID', 'Subdimensión', 'Score', 'Justificación']],
          body: subTableData,
          startY: y,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 40 },
            2: { cellWidth: 18, halign: 'center' },
          },
          alternateRowStyles: { fillColor: [245, 245, 250] },
          margin: { left: 14, right: 14 },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }
    }

    // --- Alertas de Desequilibrio ---
    const alerts = detectImbalances(dimScores as any);
    if (alerts.length > 0) {
      if (y > 240) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(14);
      doc.setTextColor(200, 50, 50);
      doc.text('Alertas de Desequilibrio', 14, y);
      y += 7;
      alerts.forEach(alert => {
        doc.setFontSize(9);
        doc.setTextColor(180, 40, 40);
        doc.text(`• ${alert.highDimension} (${alert.highScore}) vs ${alert.lowDimension} (${alert.lowScore}) — Brecha: ${alert.gap}`, 14, y);
        y += 5;
        doc.setTextColor(100, 100, 100);
        const msgLines = doc.splitTextToSize(alert.message, pageWidth - 32);
        doc.text(msgLines, 18, y);
        y += msgLines.length * 4 + 4;
      });
    }

    // --- Historial ---
    if (diagnosticHistory.length > 1) {
      if (y > 230) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Historial de Diagnósticos', 14, y);
      y += 4;

      const histTableData = diagnosticHistory.map(item => [
        item.completed_at ? format(new Date(item.completed_at), 'dd/MM/yyyy', { locale: es }) : 'N/A',
        item.overall_level_name || `Nivel ${item.overall_level}`,
        `${item.completion_percentage || 0}%`,
        item.company_name || '',
      ]);

      autoTable(doc, {
        head: [['Fecha', 'Nivel', 'Completado', 'Empresa']],
        body: histTableData,
        startY: y,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        margin: { left: 14, right: 14 },
      });
    }

    // --- Footer ---
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text(`LIDER-IA — Página ${i} de ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    }

    doc.save(`diagnostico-lider-ia-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exportado exitosamente');
  };

  const isEnterpriseInfoComplete = 
    enterpriseInfo.nombreEmpresa && 
    enterpriseInfo.sector && 
    enterpriseInfo.numEmpleados &&
    enterpriseInfo.responsable;

  const handleEnterpriseInfoChange = (field: keyof EnterpriseInfo, value: string) => {
    setEnterpriseInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAnswerChange = (questionId: string, text: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { text, cannotAnswer: false },
    }));
  };

  const handleCannotAnswerChange = (questionId: string, checked: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { text: checked ? '' : (prev[questionId]?.text || ''), cannotAnswer: checked },
    }));
  };

  const getAnsweredForDimension = (dimIndex: number) => {
    const dim = dimensions[dimIndex];
    let count = 0;
    for (const sub of dim.subSections) {
      for (const q of sub.questions) {
        const ans = answers[q.id];
        if (ans && (ans.text.trim().length > 0 || ans.cannotAnswer)) count++;
      }
    }
    return count;
  };

  const currentDimQuestionCount = getDimensionQuestionCount(currentDimension.id);
  const currentDimAnswered = getAnsweredForDimension(currentDimensionIndex);
  const dimensionProgress = currentDimQuestionCount > 0 ? (currentDimAnswered / currentDimQuestionCount) * 100 : 0;

  const totalAnswered = Object.values(answers).filter(a => a.text.trim().length > 0 || a.cannotAnswer).length;
  const overallProgress = totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0;

  const handleNextDimension = () => {
    if (currentDimensionIndex < dimensions.length - 1) {
      setCurrentDimensionIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleFinish();
    }
  };

  const handlePreviousDimension = () => {
    if (currentDimensionIndex > 0) {
      setCurrentDimensionIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStep('info');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFinish = async () => {
    setIsEvaluating(true);
    
    try {
      // Try AI evaluation first
      const aiResult = await evaluateDiagnostic.mutateAsync({
        answers,
        companyName: enterpriseInfo.nombreEmpresa,
        sector: enterpriseInfo.sector,
      });

      const diagnosticResult = buildResultFromAIScores(answers, aiResult.dimensions);
      setResult(diagnosticResult);

      // Save to DB
      const targetOrgId = effectiveOrg || selectedOrg;
      if (targetOrgId && targetOrgId !== 'all') {
        await saveDiagnostic.mutateAsync({
          organizationId: targetOrgId,
          companyName: enterpriseInfo.nombreEmpresa,
          sector: enterpriseInfo.sector,
          employeeCount: enterpriseInfo.numEmpleados,
          answers,
          overallLevel: diagnosticResult.overallLevel,
          overallLevelName: diagnosticResult.overallLevelName,
          dimensionScores: diagnosticResult.dimensionScores,
          completionPercentage: diagnosticResult.completionPercentage,
        });
      }

      if (draftKey) clearDraft(draftKey);
      setStep('complete');
    } catch (err) {
      console.error('AI evaluation failed, using fallback:', err);
      // Fallback to completion-based scoring
      const diagnosticResult = calculateEnterpriseResult(answers);
      setResult(diagnosticResult);

      const targetOrgId = effectiveOrg || selectedOrg;
      if (targetOrgId && targetOrgId !== 'all') {
        try {
          await saveDiagnostic.mutateAsync({
            organizationId: targetOrgId,
            companyName: enterpriseInfo.nombreEmpresa,
            sector: enterpriseInfo.sector,
            employeeCount: enterpriseInfo.numEmpleados,
            answers,
            overallLevel: diagnosticResult.overallLevel,
            overallLevelName: diagnosticResult.overallLevelName,
            dimensionScores: diagnosticResult.dimensionScores,
            completionPercentage: diagnosticResult.completionPercentage,
          });
        } catch (saveErr) {
          console.error('Save failed:', saveErr);
        }
      }

      if (draftKey) clearDraft(draftKey);
      setStep('complete');
      toast.warning('Diagnóstico completado con evaluación básica (la evaluación con IA no estuvo disponible)');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleStartNewDiagnostic = () => {
    setViewMode('diagnostic');
    setStep('info');
    // Pre-fill from org if available, otherwise blank
    const empCount = orgDetails?.employee_count;
    let empRange = '';
    if (empCount) {
      if (empCount <= 10) empRange = '1-10';
      else if (empCount <= 50) empRange = '11-50';
      else if (empCount <= 200) empRange = '51-200';
      else if (empCount <= 500) empRange = '201-500';
      else if (empCount <= 1000) empRange = '501-1000';
      else empRange = '1000+';
    }
    setEnterpriseInfo({
      nombreEmpresa: orgDetails?.name || '',
      sector: orgDetails?.sector || '',
      numEmpleados: empRange,
      responsable: orgDetails?.contact_name || '',
    });
    setAnswers({});
    setCurrentDimensionIndex(0);
    setResult(null);
  };

  const handleBackToResults = () => {
    setViewMode('results');
    setStep('info');
  };

  const getLevelColor = (level: number) => {
    if (level <= 1) return 'text-destructive';
    if (level <= 2) return 'text-accent';
    if (level <= 3) return 'text-warning';
    if (level <= 4) return 'text-primary';
    return 'text-success';
  };

  const getLevelBgColor = (level: number) => {
    if (level <= 1) return 'bg-destructive/10 border-destructive/30';
    if (level <= 2) return 'bg-accent/10 border-accent/30';
    if (level <= 3) return 'bg-warning/10 border-warning/30';
    if (level <= 4) return 'bg-primary/10 border-primary/30';
    return 'bg-success/10 border-success/30';
  };

  const getRadarData = (scores: { dimensionId: string; name: string; score: number }[]) => {
    return scores.map(s => ({
      dimension: s.dimensionId,
      fullName: s.name,
      score: s.score,
      fullMark: 5,
    }));
  };

  // Imbalance alerts for latest result
  const latestImbalanceAlerts = (() => {
    const scores = getLatestDimensionScores();
    if (!scores.length) return [];
    return detectImbalances(scores as any);
  })();

  // ==================== RESULTS VIEW ====================
  if (viewMode === 'results') {
    const latestDimScores = getLatestDimensionScores();
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Diagnóstico Empresarial LIDER-IA</h1>
            <p className="text-muted-foreground">
              {isSuperAdmin && showAllOrgs 
                ? 'Diagnósticos de todas las organizaciones' 
                : `Diagnósticos de ${getOrgName()}`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
            {latestResult && (
              <Button variant="outline" onClick={handleExportPDF}>
                <Download className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            )}
            <Button onClick={handleStartNewDiagnostic} className="gradient-primary">
              <RefreshCw className="w-4 h-4 mr-2" />
              Nuevo Diagnóstico
            </Button>
          </div>
        </div>

        {/* Resume draft prompt in results view */}
        {draftPrompt && (
          <Card className="glass-card border-primary/40">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <p className="font-semibold text-foreground">Tienes un diagnóstico empresarial en progreso</p>
                <p className="text-sm text-muted-foreground">
                  Última actualización: {formatDistanceToNow(new Date(draftPrompt.updatedAt), { addSuffix: true, locale: es })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDiscardEnterpriseDraft}>Empezar de nuevo</Button>
                <Button size="sm" onClick={handleResumeEnterpriseDraft}>Continuar</Button>
              </div>
            </CardContent>
          </Card>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nivel Global</p>
                  <p className="text-xl font-bold text-foreground">{latestResult?.overall_level_name || 'Sin evaluar'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completado</p>
                  <p className="text-xl font-bold text-foreground">{latestResult?.completion_percentage || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <History className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Diagnósticos</p>
                  <p className="text-xl font-bold text-foreground">{diagnosticHistory.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Último</p>
                  <p className="text-xl font-bold text-foreground">
                    {latestResult?.completed_at ? format(new Date(latestResult.completed_at), 'dd MMM yyyy', { locale: es }) : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Imbalance Alerts */}
        {latestImbalanceAlerts.length > 0 && (
          <div className="space-y-2">
            {latestImbalanceAlerts.map((alert, idx) => (
              <Card key={idx} className="border-destructive/40 bg-destructive/5">
                <CardContent className="py-3 flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">
                      Desequilibrio: {alert.highDimension} ({alert.highScore}) vs {alert.lowDimension} ({alert.lowScore}) — Brecha de {alert.gap} niveles
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="current" className="space-y-4">
          <TabsList>
            <TabsTrigger value="current">Resultado Actual</TabsTrigger>
            <TabsTrigger value="subdimensions">Subdimensiones</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
            <TabsTrigger value="recommendations">Recomendaciones</TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="space-y-4">
            {latestResult && latestDimScores.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Radar Chart */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-lg">Perfil de Madurez LIDER-IA</CardTitle>
                    <CardDescription>Puntuación por dimensión (escala 1-5)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={getRadarData(latestDimScores as any)}>
                          <PolarGrid stroke="hsl(var(--border))" />
                          <PolarAngleAxis dataKey="dimension" tick={{ fill: 'hsl(var(--foreground))', fontSize: 14, fontWeight: 600 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                          <Radar name="Puntuación" dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.25} strokeWidth={2} />
                          <RechartsTooltip 
                            formatter={(value: number, _name: string, props: { payload: { fullName: string } }) => [`${value}/5`, props.payload.fullName]}
                            contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Dimension Scores */}
                <Card className={`glass-card border-2 ${getLevelBgColor(latestResult.overall_level || 1)}`}>
                  <CardContent className="pt-6 space-y-5">
                    <div className="text-center space-y-2">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getLevelBgColor(latestResult.overall_level || 1)}`}>
                        <Trophy className={`w-8 h-8 ${getLevelColor(latestResult.overall_level || 1)}`} />
                      </div>
                      <h2 className={`text-2xl font-bold ${getLevelColor(latestResult.overall_level || 1)}`}>
                        Nivel {latestResult.overall_level}: {latestResult.overall_level_name}
                      </h2>
                      <p className="text-sm text-muted-foreground">{maturityLevelNames[latestResult.overall_level || 1]?.description}</p>
                    </div>

                    <div className="space-y-3">
                      {latestDimScores.map((ds: any) => {
                        const dim = dimensions.find(d => d.id === ds.dimensionId);
                        if (!dim) return null;
                        return (
                          <div key={ds.dimensionId} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white ${dimensionBgColors[ds.dimensionId]}`}>
                              {ds.dimensionId}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate">{dim.fullName}</span>
                                <span className="text-sm font-bold">{ds.score}/5</span>
                              </div>
                              <Progress value={(ds.score / 5) * 100} className="h-2" />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-2 text-center text-sm text-muted-foreground space-y-1">
                      <p>Empresa: <span className="font-medium text-foreground">{latestResult.company_name}</span></p>
                      <p>Completado: <span className="font-medium text-foreground">{latestResult.completion_percentage}%</span></p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="glass-card">
                <CardContent className="py-12 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold">Sin diagnóstico previo</h3>
                    <p className="text-muted-foreground">Realiza tu primer diagnóstico LIDER-IA para conocer el perfil de madurez de tu organización.</p>
                  </div>
                  <Button onClick={handleStartNewDiagnostic} className="gradient-primary">
                    <Target className="w-4 h-4 mr-2" />
                    Iniciar Diagnóstico
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Subdimension Scores Tab */}
          <TabsContent value="subdimensions" className="space-y-4">
            {latestResult && latestDimScores.length > 0 ? (
              <div className="space-y-4">
                {latestDimScores.map((ds: any) => {
                  const dim = dimensions.find(d => d.id === ds.dimensionId);
                  if (!dim) return null;
                  const subScores = ds.subDimensionScores || [];
                  return (
                    <Card key={ds.dimensionId} className="glass-card">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white ${dimensionBgColors[ds.dimensionId]}`}>
                            {ds.dimensionId}
                          </div>
                          {dim.fullName}
                          <Badge variant="outline" className="ml-auto">{ds.score}/5</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {subScores.length > 0 ? subScores.map((sub: any) => (
                          <div key={sub.subDimensionId} className="p-3 rounded-lg border bg-muted/20">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium flex items-center gap-2">
                                <Badge variant="outline" className="font-mono text-xs">{sub.subDimensionId}</Badge>
                                {sub.subDimensionName}
                              </span>
                              <span className="text-sm font-bold">{sub.score}/5</span>
                            </div>
                            <Progress value={(sub.score / 5) * 100} className="h-1.5 mb-2" />
                            {sub.justification && (
                              <p className="text-xs text-muted-foreground">{sub.justification}</p>
                            )}
                          </div>
                        )) : (
                          <p className="text-sm text-muted-foreground">Sin detalle por subdimensión disponible para este diagnóstico.</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="glass-card">
                <CardContent className="py-8 text-center text-muted-foreground">
                  Completa un diagnóstico para ver el detalle por subdimensión.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg">Historial de Diagnósticos</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : diagnosticHistory.length > 0 ? (
                  <div className="space-y-4">
                    {diagnosticHistory.map((item, index) => (
                      <div 
                        key={item.id}
                        className={`flex items-center justify-between p-4 rounded-xl border ${
                          index === 0 ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getLevelBgColor(item.overall_level || 1)}`}>
                            <Trophy className={`w-5 h-5 ${getLevelColor(item.overall_level || 1)}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">Nivel {item.overall_level}: {item.overall_level_name}</p>
                              {index === 0 && <Badge variant="default" className="text-xs">Actual</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {item.completed_at ? format(new Date(item.completed_at), "dd 'de' MMMM, yyyy", { locale: es }) : 'N/A'}
                              {item.company_name ? ` • ${item.company_name}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{item.completion_percentage || 0}%</p>
                          <p className="text-xs text-muted-foreground">{item.sector || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No hay diagnósticos previos</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recommendations" className="space-y-4">
            {latestResult && latestDimScores.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary" />
                      Dimensiones Prioritarias
                    </CardTitle>
                    <CardDescription>Pilares con menor puntuación que requieren atención</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(latestDimScores as any[])
                      .slice()
                      .sort((a: any, b: any) => a.score - b.score)
                      .slice(0, 3)
                      .map((ds: any) => {
                        const dim = dimensions.find(d => d.id === ds.dimensionId);
                        if (!dim) return null;
                        return (
                          <div key={ds.dimensionId} className={`p-3 rounded-lg border ${dimensionColors[ds.dimensionId]}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{dim.letter}: {dim.fullName}</span>
                              <Badge variant="outline">{ds.score}/5</Badge>
                            </div>
                            <p className="text-xs opacity-80">{dim.maturityLevels[ds.score]}</p>
                          </div>
                        );
                      })}
                  </CardContent>
                </Card>

                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-accent" />
                      Próximo Nivel por Dimensión
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(latestDimScores as any[]).map((ds: any) => {
                      const dim = dimensions.find(d => d.id === ds.dimensionId);
                      if (!dim) return null;
                      const nextLevel = Math.min(5, ds.score + 1);
                      return (
                        <div key={ds.dimensionId} className="flex items-center justify-between text-sm">
                          <span className="font-medium">{dim.letter}: {dim.name}</span>
                          <span className="text-muted-foreground">
                            {ds.score < 5 ? `→ Nivel ${nextLevel}` : '✓ Máximo'}
                          </span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                <Card className="glass-card md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-warning" />
                      Niveles de Madurez - Referencia
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                      {[1, 2, 3, 4, 5].map(level => (
                        <div key={level} className={`p-3 rounded-xl border ${
                          (latestResult.overall_level || 0) === level ? getLevelBgColor(level) + ' ring-2 ring-primary' : 'bg-muted/30 border-border'
                        }`}>
                          <h4 className="font-bold text-sm mb-1">Nivel {level}</h4>
                          <p className="text-xs font-medium mb-1">{maturityLevelNames[level].name}</p>
                          <p className="text-xs text-muted-foreground">{maturityLevelNames[level].description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="glass-card">
                <CardContent className="py-12 text-center space-y-4">
                  <p className="text-muted-foreground">Completa un diagnóstico para ver recomendaciones personalizadas</p>
                  <Button onClick={handleStartNewDiagnostic} className="gradient-primary">
                    <Target className="w-4 h-4 mr-2" />
                    Iniciar Diagnóstico
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ==================== DIAGNOSTIC FLOW ====================
  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBackToResults}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Marco L I D E R - IA</h1>
          <p className="text-muted-foreground">Instrumento Diagnóstico de Madurez</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
          <div className={`flex items-center gap-1 ${step === 'info' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 'info' ? 'bg-primary text-primary-foreground' : 'bg-success text-success-foreground'
            }`}>
              {step !== 'info' ? <CheckCircle2 className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
            </div>
            <span className="text-xs font-medium hidden sm:inline">Info</span>
          </div>

          {dimensions.map((dim, i) => (
            <div key={dim.id} className="flex items-center">
              <div className="w-4 sm:w-8 h-0.5 bg-border" />
              <div className={`flex items-center gap-1 ${
                step === 'dimensions' && currentDimensionIndex === i ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 'dimensions' && currentDimensionIndex === i ? dimensionBgColors[dim.id] + ' text-white' :
                  (step === 'dimensions' && currentDimensionIndex > i) || step === 'complete' ? 'bg-success text-success-foreground' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {(step === 'dimensions' && currentDimensionIndex > i) || step === 'complete' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : dim.letter}
                </div>
                <span className="text-xs font-medium hidden lg:inline">{dim.name}</span>
              </div>
            </div>
          ))}

          <div className="flex items-center">
            <div className="w-4 sm:w-8 h-0.5 bg-border" />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'complete' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              <Trophy className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Overall progress */}
        {step === 'dimensions' && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Progreso general: {totalAnswered} de {totalQuestions} preguntas</span>
              <span>{Math.round(overallProgress)}%</span>
            </div>
            <Progress value={overallProgress} className="h-1.5" />
          </div>
        )}

        {/* Resume draft prompt */}
        {draftPrompt && (
          <Card className="glass-card border-primary/40">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <p className="font-semibold text-foreground">Tienes un diagnóstico empresarial en progreso</p>
                <p className="text-sm text-muted-foreground">
                  Última actualización: {formatDistanceToNow(new Date(draftPrompt.updatedAt), { addSuffix: true, locale: es })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDiscardEnterpriseDraft}>Empezar de nuevo</Button>
                <Button size="sm" onClick={handleResumeEnterpriseDraft}>Continuar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enterprise Info Step */}
        {step === 'info' && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-xl">Información de la Empresa</CardTitle>
              <CardDescription>Complete los datos de la organización a evaluar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="nombreEmpresa">Nombre de la empresa *</Label>
                <Input id="nombreEmpresa" placeholder="Ingresa el nombre de la empresa" value={enterpriseInfo.nombreEmpresa} onChange={(e) => handleEnterpriseInfoChange('nombreEmpresa', e.target.value)} className="bg-muted/50" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sector">Sector *</Label>
                <Input id="sector" placeholder="Ingresa el sector" value={enterpriseInfo.sector} onChange={(e) => handleEnterpriseInfoChange('sector', e.target.value)} className="bg-muted/50" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numEmpleados">Número de empleados *</Label>
                <Select value={enterpriseInfo.numEmpleados} onValueChange={(value) => handleEnterpriseInfoChange('numEmpleados', value)}>
                  <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Selecciona el rango" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10</SelectItem>
                    <SelectItem value="11-50">11-50</SelectItem>
                    <SelectItem value="51-200">51-200</SelectItem>
                    <SelectItem value="201-500">201-500</SelectItem>
                    <SelectItem value="501-1000">501-1000</SelectItem>
                    <SelectItem value="1000+">Más de 1000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="responsable">Responsable de la evaluación *</Label>
                <Input id="responsable" placeholder="Nombre del responsable" value={enterpriseInfo.responsable} onChange={(e) => handleEnterpriseInfoChange('responsable', e.target.value)} className="bg-muted/50" />
              </div>
              <Button onClick={() => { setStep('dimensions'); setCurrentDimensionIndex(0); }} disabled={!isEnterpriseInfoComplete} className="w-full mt-4 gradient-primary">
                Comenzar Evaluación
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Dimensions Step */}
        {step === 'dimensions' && (
          <div className="space-y-4">
            <Card className={`border-2 ${dimensionColors[currentDimension.id]}`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white ${dimensionBgColors[currentDimension.id]}`}>
                    {currentDimension.letter}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold">Dimensión {currentDimension.letter}: {currentDimension.fullName}</h2>
                    <p className="text-sm text-muted-foreground">{currentDimension.description}</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium">{currentDimAnswered}/{currentDimQuestionCount}</p>
                    <p className="text-xs text-muted-foreground">respondidas</p>
                  </div>
                </div>
                <Progress value={dimensionProgress} className="h-1.5 mt-3" />
              </CardContent>
            </Card>

            {currentDimension.subSections.map((sub) => (
              <Card key={sub.id} className="glass-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">{sub.id}</Badge>
                    {sub.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sub.questions.map((q) => {
                    const ans = answers[q.id] || { text: '', cannotAnswer: false };
                    const isAnswered = ans.text.trim().length > 0 || ans.cannotAnswer;
                    return (
                      <div key={q.id} className={`p-4 rounded-lg border ${isAnswered ? 'border-success/30 bg-success/5' : 'border-border bg-muted/10'}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-xs font-mono text-muted-foreground mt-0.5 flex-shrink-0">{q.id}</span>
                          <p className="text-sm font-medium leading-relaxed">{q.question}</p>
                          {isAnswered && <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />}
                        </div>
                        <Textarea
                          placeholder="Escribe tu respuesta aquí..."
                          value={ans.text}
                          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                          disabled={ans.cannotAnswer}
                          className={`min-h-[80px] bg-background/50 border-border resize-none text-sm ${ans.cannotAnswer ? 'opacity-50' : ''}`}
                        />
                        <div className="flex items-center space-x-2 mt-2">
                          <Checkbox
                            id={`cannot-${q.id}`}
                            checked={ans.cannotAnswer}
                            onCheckedChange={(checked) => handleCannotAnswerChange(q.id, checked === true)}
                          />
                          <Label htmlFor={`cannot-${q.id}`} className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            No puedo responder
                          </Label>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}

            {/* Navigation */}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handlePreviousDimension} className="flex-1">
                <ChevronLeft className="w-4 h-4 mr-2" />
                {currentDimensionIndex === 0 ? 'Información' : `Dimensión ${dimensions[currentDimensionIndex - 1].letter}`}
              </Button>
              <Button onClick={handleNextDimension} disabled={isEvaluating} className="flex-1 gradient-primary">
                {isEvaluating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Evaluando con IA...
                  </>
                ) : currentDimensionIndex === dimensions.length - 1 ? (
                  'Evaluar con IA'
                ) : (
                  `Dimensión ${dimensions[currentDimensionIndex + 1].letter}`
                )}
                {!isEvaluating && <ChevronRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && result && (
          <div className="space-y-6">
            <Card className={`glass-card border-2 ${getLevelBgColor(result.overallLevel)}`}>
              <CardContent className="pt-8 pb-8 text-center space-y-6">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${getLevelBgColor(result.overallLevel)}`}>
                  <Trophy className={`w-10 h-10 ${getLevelColor(result.overallLevel)}`} />
                </div>
                <div className="space-y-2">
                  <h2 className={`text-3xl font-bold ${getLevelColor(result.overallLevel)}`}>
                    Nivel {result.overallLevel}: {result.overallLevelName}
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {maturityLevelNames[result.overallLevel].description}
                  </p>
                </div>
                <div className="pt-4 space-y-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-bold text-foreground">{result.completionPercentage}%</span>
                    <span className="text-muted-foreground">completado</span>
                  </div>
                  <div className="max-w-xs mx-auto">
                    <Progress value={result.completionPercentage} className="h-3" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {result.totalAnswered} de {result.totalQuestions} preguntas respondidas
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Imbalance Alerts */}
            {result.imbalanceAlerts.length > 0 && (
              <Card className="border-destructive/40 bg-destructive/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-destructive">
                    <ShieldAlert className="w-5 h-5" />
                    Alertas de Desequilibrio
                  </CardTitle>
                  <CardDescription>La fortaleza técnica no compensa la debilidad ética</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {result.imbalanceAlerts.map((alert, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-destructive/20 bg-background/50">
                      <p className="text-sm font-medium">
                        {alert.highDimension} ({alert.highScore}) vs {alert.lowDimension} ({alert.lowScore}) — Brecha de {alert.gap} niveles
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Radar Chart */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg">Perfil de Madurez por Dimensión</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={getRadarData(result.dimensionScores)}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="dimension" tick={{ fill: 'hsl(var(--foreground))', fontSize: 14, fontWeight: 600 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                      <Radar name="Puntuación" dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.25} strokeWidth={2} />
                      <RechartsTooltip 
                        formatter={(value: number, _name: string, props: { payload: { fullName: string } }) => [`${value}/5`, props.payload.fullName]}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-4">
                  {result.dimensionScores.map(ds => {
                    const dim = dimensions.find(d => d.id === ds.dimensionId)!;
                    return (
                      <div key={ds.dimensionId} className={`p-3 rounded-lg border text-center ${dimensionColors[ds.dimensionId]}`}>
                        <div className="text-2xl font-bold">{ds.score}</div>
                        <div className="text-xs font-medium">{dim.letter}: {dim.name}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Subdimension detail */}
                {result.dimensionScores.some(ds => ds.subDimensionScores?.length) && (
                  <div className="mt-6 space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Detalle por Subdimensión</h3>
                    {result.dimensionScores.map(ds => 
                      ds.subDimensionScores?.map(sub => (
                        <div key={sub.subDimensionId} className="flex items-center justify-between text-sm p-2 rounded border bg-muted/20">
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">{sub.subDimensionId}</Badge>
                            <span className="truncate">{sub.subDimensionName}</span>
                          </span>
                          <span className="font-bold">{sub.score}/5</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleStartNewDiagnostic} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Nuevo Diagnóstico
              </Button>
              <Button onClick={handleBackToResults} className="flex-1 gradient-primary">
                Ver Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
